import { TooltipMotionState } from '../components/SitePerformanceChartTooltip/SitePerformanceChartTooltip.types';
import {
  SPRING_MASS,
  SPRING_STIFFNESS,
  SPRING_DAMPING,
  MOTION_SETTLE_THRESHOLD,
} from 'app/modules/site-management/widgets/fleet-dashboard/constants/SitePerformanceChart.constants';

const tooltipMotionState = new WeakMap<HTMLDivElement, TooltipMotionState>();

const getMotionState = (element: HTMLDivElement, initialX: number, initialY: number): TooltipMotionState => {
  const existingState = tooltipMotionState.get(element);

  if (existingState) {
    return existingState;
  }

  const state: TooltipMotionState = {
    element,
    x: initialX,
    y: initialY,
    vx: 0,
    vy: 0,
    targetX: initialX,
    targetY: initialY,
    rafId: null,
  };

  tooltipMotionState.set(element, state);

  return state;
};

const applyTransform = (state: TooltipMotionState): void => {
  state.element.style.transform = `translate3d(${state.x}px, ${state.y}px, 0)`;
};

const stepAxis = (position: number, velocity: number, target: number, delta: number): [number, number] => {
  const acceleration = (-SPRING_STIFFNESS * (position - target) - SPRING_DAMPING * velocity) / SPRING_MASS;
  const nextVelocity = velocity + acceleration * delta;
  const nextPosition = position + nextVelocity * delta;

  return [nextPosition, nextVelocity];
};

const isMotionSettled = (state: TooltipMotionState): boolean =>
  Math.abs(state.x - state.targetX) < MOTION_SETTLE_THRESHOLD &&
  Math.abs(state.y - state.targetY) < MOTION_SETTLE_THRESHOLD &&
  Math.abs(state.vx) < MOTION_SETTLE_THRESHOLD &&
  Math.abs(state.vy) < MOTION_SETTLE_THRESHOLD;

const startMotionLoop = (state: TooltipMotionState): void => {
  if (state.rafId != null) {
    return;
  }

  let lastTime = performance.now();

  const tick = (now: number): void => {
    const delta = Math.min((now - lastTime) / 1000, 0.064);
    lastTime = now;

    [state.x, state.vx] = stepAxis(state.x, state.vx, state.targetX, delta);
    [state.y, state.vy] = stepAxis(state.y, state.vy, state.targetY, delta);

    applyTransform(state);

    if (isMotionSettled(state)) {
      state.rafId = null;
      return;
    }

    state.rafId = requestAnimationFrame(tick);
  };

  state.rafId = requestAnimationFrame(tick);
};

export const stopTooltipMotion = (element: HTMLDivElement): void => {
  const state = tooltipMotionState.get(element);

  if (state?.rafId != null) {
    cancelAnimationFrame(state.rafId);
    state.rafId = null;
  }
};

export const setTooltipMotionTarget = (
  element: HTMLDivElement,
  targetX: number,
  targetY: number,
  immediate = false
): void => {
  const state = getMotionState(element, targetX, targetY);

  state.targetX = targetX;
  state.targetY = targetY;

  if (immediate) {
    stopTooltipMotion(element);
    state.x = targetX;
    state.y = targetY;
    state.vx = 0;
    state.vy = 0;
    applyTransform(state);
    return;
  }

  startMotionLoop(state);
};
